import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getTournamentRounds } from '../../services/tournament.service';

const toNum = (v) => (v === null || v === undefined ? null : Number(v));

const fmtNormal = (res) => {
  const r = toNum(res);
  if (r === 1) return '1';
  if (r === 0) return '0';
  if (r === 2) return '½';
  return '!';
};

// Devuelve { tipo: 'normal'|'ausencia'|'bye', resB, resN }
const getResultDisplay = (p) => {
  const tipoB = toNum(p.par_tipo_res_blancas);
  const tipoN = toNum(p.par_tipo_res_negras);
  const resB = toNum(p.par_resultado_blancas);
  const resN = toNum(p.par_resultado_negras);

  if (tipoB === 3 && tipoN === 3) {
    return { tipo: 'bye', resB: fmtNormal(resB) };
  }
  if (tipoB === 2 && tipoN === 2) {
    return { tipo: 'ausencia', resB: resB === 1 ? '+' : '-', resN: resN === 1 ? '+' : '-' };
  }
  // Normal o fallback cuando tipo_res es null (ej: backend no reiniciado)
  return { tipo: 'normal', resB: fmtNormal(resB), resN: fmtNormal(resN) };
};

const headerSx = {
  background: '#565656',
  color: '#e2e8f0',
  fontWeight: 700,
  letterSpacing: 0.5,
};

const cellSx = {
  background: '#9295a9',
  color: '#1a1f2e',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0,0,0,0.15)',
};

const resultCellSx = {
  ...cellSx,
  textAlign: 'center',
  fontWeight: 700,
  fontFamily: 'monospace',
  fontSize: '0.95rem',
  width: 80,
};

const TournamentRounds = ({ torId }) => {
  const [rounds, setRounds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchRounds = async () => {
      setIsLoading(true);
      try {
        const data = await getTournamentRounds(torId);
        setRounds(data);
      } catch (e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRounds();
  }, [torId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c8cf8' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ py: 3 }}>
        Error al cargar las rondas.
      </Typography>
    );
  }

  if (!rounds.length) {
    return (
      <Typography sx={{ color: '#9aa5b4', py: 3 }}>
        No hay rondas registradas para este torneo.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rounds.map((round) => (
        <Box key={round.numero}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, maxWidth: 700, mx: 'auto' }}>
            <Typography
              variant="h6"
              sx={{ color: '#e2e8f0', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Ronda {round.numero}
            </Typography>
            <Divider
              sx={{ flex: 1, borderColor: 'rgba(124,140,248,0.25)' }}
            />
            <Typography variant="caption" sx={{ color: '#9aa5b4' }}>
              {(() => {
                const n = round.partidas.filter(p => getResultDisplay(p).tipo === 'normal').length;
                return `${n} partida${n !== 1 ? 's' : ''}`;
              })()}
            </Typography>
          </Box>

          {round.partidas.length === 0 ? (
            <Typography sx={{ color: '#9aa5b4', fontSize: '0.85rem', pl: 1 }}>
              Sin partidas registradas
            </Typography>
          ) : (
            <Box sx={{ maxWidth: 700, mx: 'auto' }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 0,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'transparent',
                }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerSx}>Blancas</TableCell>
                      <TableCell colSpan={2} sx={{ ...headerSx, textAlign: 'center', width: 160 }}>
                        Resultado
                      </TableCell>
                      <TableCell sx={headerSx}>Negras</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {round.partidas.map((p) => {
                      const rd = getResultDisplay(p);

                      if (rd.tipo === 'bye') {
                        return (
                          <TableRow key={p.par_id} hover>
                            <TableCell sx={cellSx}>{p.jugador_blancas ?? '—'}</TableCell>
                            <TableCell
                              colSpan={2}
                              sx={{ ...resultCellSx, letterSpacing: 2 }}
                            >
                              {rd.resB}
                            </TableCell>
                            <TableCell sx={{ ...cellSx, fontStyle: 'italic', color: '#334155' }}>
                              bye
                            </TableCell>
                          </TableRow>
                        );
                      }

                      if (rd.tipo === 'ausencia') {
                        return (
                          <TableRow key={p.par_id} hover>
                            <TableCell sx={cellSx}>{p.jugador_blancas ?? '—'}</TableCell>
                            <TableCell sx={{ ...resultCellSx, color: rd.resB === '+' ? '#15803d' : '#ef4444' }}>
                              {rd.resB}
                            </TableCell>
                            <TableCell sx={{ ...resultCellSx, color: rd.resN === '+' ? '#15803d' : '#ef4444' }}>
                              {rd.resN}
                            </TableCell>
                            <TableCell sx={cellSx}>{p.jugador_negras ?? '—'}</TableCell>
                          </TableRow>
                        );
                      }

                      // Normal (y fallback unknown)
                      return (
                        <TableRow key={p.par_id} hover>
                          <TableCell sx={cellSx}>{p.jugador_blancas ?? '—'}</TableCell>
                          <TableCell sx={resultCellSx}>{rd.resB ?? '!'}</TableCell>
                          <TableCell sx={resultCellSx}>{rd.resN ?? '!'}</TableCell>
                          <TableCell sx={cellSx}>{p.jugador_negras ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

        </Box>
      ))}
    </Box>
  );
};

export default TournamentRounds;
